import React, { useState, useEffect } from 'react';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { Button, CircularProgress, Box } from '@mui/material';
import { useResearcherSignature } from '../../../hooks/useResearcherSignature';
import { convertBlobToBase64 } from '../utils/imageUtils';

interface PDFDownloadButtonWithSignatureProps {
  /** Function that receives signatureBase64 (or null) and returns the PDF document */
  children: (signatureBase64: string | null) => React.ReactElement;
  /** PDF filename (without .pdf extension) */
  filename: string;
  /** Investigation UUID for signature fetching */
  uuidInvestigation: string;
  /** Researcher UUID whose signature to fetch (optional) */
  uuidResearcher?: string;
  /** Button text/content when ready */
  label?: React.ReactNode;
  /** Button variant */
  variant?: 'text' | 'outlined' | 'contained';
  /** Button size */
  size?: 'small' | 'medium' | 'large';
  /** Additional button styles */
  style?: React.CSSProperties;
  /** Button color */
  color?: 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
}

/**
 * PDF Download Button with Signature Fetching
 * 
 * Automatically fetches researcher signature from API and converts it to base64
 * before enabling PDF download. Shows loading state while fetching and converting.
 * 
 * @example
 * ```tsx
 * <PDFDownloadButtonWithSignature
 *   uuidInvestigation="inv-123"
 *   uuidResearcher="researcher-456"
 *   filename="submission-report"
 *   label="Download PDF"
 * >
 *   {(signatureBase64) => (
 *     <SubmissionPDFDocument 
 *       {...props}
 *       signatureBase64={signatureBase64}
 *     />
 *   )}
 * </PDFDownloadButtonWithSignature>
 * ```
 */
export const PDFDownloadButtonWithSignature: React.FC<PDFDownloadButtonWithSignatureProps> = ({
  children,
  filename,
  uuidInvestigation,
  uuidResearcher,
  label = 'Download PDF',
  variant = 'contained',
  size = 'medium',
  style,
  color = 'primary',
}) => {
  // Fetch signature URL from API
  const { signatureUrl, loading: loadingSignature, error } = useResearcherSignature({
    uuidInvestigation,
    uuidResearcher: uuidResearcher || '',
    enabled: !!uuidResearcher && !!uuidInvestigation,
  });

  // State for base64-converted signature (React PDF needs base64, not URLs)
  const [signatureBase64, setSignatureBase64] = useState<string | null>(null);
  const [convertingImage, setConvertingImage] = useState(false);
  const [conversionError, setConversionError] = useState<string | null>(null);

  // Convert signature URL to base64 when it's available
  useEffect(() => {
    if (signatureUrl) {
      // Check if it's already base64 (starts with "data:")
      if (signatureUrl.startsWith('data:')) {
        setSignatureBase64(signatureUrl);
        setConversionError(null);
        setConvertingImage(false);
      } else {
        // It's a URL - need to convert to base64
        setConvertingImage(true);
        setConversionError(null);
        
        convertBlobToBase64(signatureUrl)
          .then(base64 => {
            setSignatureBase64(base64);
            setConversionError(null);
          })
          .catch(err => {
            const errorMsg = 'Failed to load signature image. The PDF will generate without signature.';
            setSignatureBase64(null);
            setConversionError(errorMsg);
          })
          .finally(() => {
            setConvertingImage(false);
          });
      }
    } else {
      setSignatureBase64(null);
      setConversionError(null);
    }
  }, [signatureUrl]);

  // Show loading state while signature is being fetched OR converted
  if (loadingSignature || convertingImage) {
    return (
      <Button 
        variant={variant} 
        size={size} 
        disabled 
        style={style}
        color={color}
      >
        <CircularProgress size={20} style={{ marginRight: 8 }} />
        {loadingSignature ? 'Loading signature...' : 'Converting image...'}
      </Button>
    );
  }

  // Generate PDF document with base64 signature (React PDF requires base64, not URLs)
  // This ensures a fresh document is created on each render with the current signature
  // Note: If signature conversion failed, PDF will still generate without signature
  return (
    <>
      <PDFDownloadLink
        key={signatureBase64 || 'no-signature'} // Force remount when signature changes
        document={children(signatureBase64)}
        fileName={`${filename}.pdf`}
        style={{ textDecoration: 'none', ...style }}
      >
        {({ loading: pdfLoading, error: pdfError }) => (
          <Button 
            variant={variant} 
            size={size} 
            disabled={pdfLoading}
            color={color}
            style={style}
          >
            {pdfLoading ? (
              <>
                <CircularProgress size={20} style={{ marginRight: 8 }} />
                Preparing PDF...
              </>
            ) : (
              label
            )}
          </Button>
        )}
      </PDFDownloadLink>
      
      {/* Show warning if signature conversion failed */}
      {conversionError && !loadingSignature && !convertingImage && (
        <Box sx={{ mt: 1, color: 'warning.main', fontSize: '0.875rem', textAlign: 'center' }}>
          ⚠️ {conversionError}
        </Box>
      )}
    </>
  );
};

export default PDFDownloadButtonWithSignature;

